import React, { FunctionComponent, useMemo, useRef } from 'react'
import { Form } from 'react-final-form'
import { defineMessage, FormattedMessage, useIntl } from 'react-intl'
import { Dimensions, GestureResponderEvent, ImageSourcePropType } from 'react-native'

import { StatusBar } from 'expo-status-bar'
import { noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { CARE_ADVISOR_EMAIL } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { VALUE_PROPS_TITLES } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/valuePropsMessages'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'

import {
  DawnBackground,
  DawnBackgroundMobile,
  DawnBackgroundMobileWeb,
  DawnBackgroundWiderAspectRatio,
} from '../../assets'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { BulletedList } from '../../atoms/bulletedList/BulletedList'
import { Display } from '../../atoms/display/Display'
import { CheckMarkIcon } from '../../atoms/icons/CheckMarkIcon'
import { Image } from '../../atoms/image/Image'
import { Link } from '../../atoms/link/Link'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { Title, Size as TitleSize } from '../../atoms/title/Title'
import { IS_WEB } from '../../constants'
import { useFloatingStyles } from '../../hooks/useFloatingStyles'
import { FindEmployerInputField } from '../../molecules/findEmployerInputField/FindEmployerInputField'
import { InputAccessory } from '../../molecules/inputAccessory/InputAccessory'
import { BodyTextSize, DisplaySize } from '../../styles/typeStyles'
import { AuroraAwareBackground } from '../../templates/auroraAwareBackground/AuroraAwareBackground'
import { Flex1View } from '../../templates/content/CommonViews'
import { FixedButtonFormPageTemplate } from '../../templates/fixedButtonFormPageTemplate/FixedButtonFormPageTemplate'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { mailTo, tID } from '../../utils/utils'

export interface FindEmployerProps {
  onSubmit: (values: FindEmployerFormValues) => void
  onBackPress?: () => void
  loading?: boolean
  focusRef?: React.RefObject<any>
}

const WELCOME_TO_LYRA_HEADER_TEXT = defineMessage({
  defaultMessage: 'Welcome to Lyra Health',
  description: 'Title above organization input',
})

const LETS_GET_STARTED_HEADER_TEXT = defineMessage({
  defaultMessage: 'Let’s get started',
  description: 'Title of page',
})

const CONTINUE_BUTTON_TEXT = defineMessage({
  defaultMessage: 'Continue',
  description:
    'Button text placed under an input field that once clicked, redirects the user to the organization’s microsite page that they are searching for.',
})

export type FindEmployerFormValues = {
  FindEmployerInputField: string
}

const FindEmployerContainer = styled(Flex1View)<{ theme: ThemeType; height?: number }>(({ theme, height }) => ({
  backgroundColor:
    theme.isAuroraTheme || (!theme.breakpoints.isMobileSized && IS_WEB)
      ? 'transparent'
      : theme.colors.backgroundPrimary,
  ...(height && { height: `${height}px` }),
  ...(IS_WEB && theme.breakpoints.isMobileSized && { marginBottom: '48px' }),
  ...(!theme.isAuroraTheme &&
    !theme.breakpoints.isMobileSized &&
    IS_WEB && {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    }),
}))

const OrganizationInputFloatingHeader = styled(Display)<{ theme: ThemeType }>({
  marginBottom: '26px',
  width: '100%',
})

const MobileSizedHeader = styled(Title)<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  marginBottom: theme.breakpoints.isMobileSized ? theme.spacing['8px'] : '26px',
}))

const BulletPointsTitle = styled(Title)<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['12px'],
}))

const OrganizationInputFloatingContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  alignItems: 'center',
  border: '1px',
  borderStyle: 'solid',
  borderColor: theme.isAuroraTheme ? 'transparent' : theme.colors.borderDefault,
  flexDirection: 'column',
  backgroundColor: theme.isAuroraTheme ? theme.colors.cardBackgroundPrimary : theme.colors.backgroundPrimary,
  borderRadius: theme.spacing['16px'],
  padding: theme.spacing['40px'],
  ...(theme.breakpoints.isMinWidthDesktopL && { width: '542px' }),
  ...(!theme.breakpoints.isMinWidthDesktopL && theme.breakpoints.isMinWidthDesktop && { width: '492px' }),
  ...(!theme.breakpoints.isMinWidthDesktop && theme.breakpoints.isMinWidthLaptop && { width: '442px' }),
  ...(!theme.breakpoints.isMinWidthLaptop && { width: '350px' }),
}))

const OrganizationInputFormContent = styled.View({
  width: '100%',
})

const FindEmployerContentContainerDesktopSized = styled.ImageBackground({
  flexGrow: 1,
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center',
})

const AuroraDesktopContentContainer = styled.View({
  flex: 1,
  flexDirection: 'row',
  width: '100%',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'flex-start',
  paddingTop: '258px', // Align with Aurora desktop header height
})

const LyraBulletPointsContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMinWidthDesktopL && { width: '542px', marginRight: '120px' }),
  ...(!theme.breakpoints.isMinWidthDesktopL &&
    theme.breakpoints.isMinWidthDesktop && { width: '492px', marginRight: theme.spacing['72px'] }),
  ...(!theme.breakpoints.isMinWidthDesktop && { width: '442px', marginRight: theme.spacing['56px'] }),
  ...(!theme.breakpoints.isMinWidthLaptop && { width: '358px', marginRight: theme.spacing['40px'] }),
}))

const BulletPointContainer = styled.View({
  flexDirection: 'column',
})

const FindEmployerContentContainerMobileSized = styled.View<{ theme: ThemeType }>({
  flex: 1,
  flexDirection: 'column',
})

const DawnImageContainer = styled.View<{ theme: ThemeType }>({
  flex: IS_WEB ? 0.5 : 0.4,
})

const DawnImageMobileSized = styled(Image)<{ updatedHeight?: number }>(({ updatedHeight }) => ({
  height: updatedHeight ? `${updatedHeight}px` : '100%',
  width: Dimensions.get('window').width,
}))

const MobileSizedFormContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flex: theme.isAuroraTheme ? 1 : IS_WEB ? 0.5 : 0.6,
  paddingTop: theme.isAuroraTheme ? '146px' : undefined,
  marginTop: theme.isAuroraTheme ? undefined : '34px',
  marginHorizontal: theme.spacing['16px'],
  marginBottom: theme.spacing['40px'],
}))

const DescriptionTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.isAuroraTheme ? theme.spacing['16px'] : undefined,
  marginBottom: theme.isAuroraTheme ? theme.spacing['16px'] : theme.spacing['8px'],
}))

const NativeMobileFormSpacing = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.isAuroraTheme ? theme.spacing['16px'] : undefined,
}))

const DesktopCardAndSupportTextContainer = styled.View({
  flexDirection: 'column',
  alignItems: 'center',
})

const CareNavigatorSupportTextContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['24px'],
  alignSelf: 'flex-start',
}))

export const FindEmployer: FunctionComponent<FindEmployerProps> = ({
  onSubmit,
  onBackPress = noop,
  loading,
  focusRef,
}) => {
  const submit = useRef(noop)
  const { formatMessage } = useIntl()
  const testID = 'FindEmployerPage'
  const { isDbtReferralEnabled } = useFlags()
  const { colors, breakpoints, isAuroraTheme } = useTheme() as ThemeType
  const { isMobileSized, isMinWidthLaptop, isMinWidthDesktop } = breakpoints
  // Shadows on web only work with StyleSheet.create. Using it with styled-components/native throws an error and breaks the app
  const floatingStyles = useFloatingStyles()

  const navBarProps = {
    onBack: onBackPress,
    isTransparent: true,
    backIconColor: colors.iconLightBackground,
    a11yFocusedElementOnLoad: focusRef,
  }

  const checkMarkFillColor = isAuroraTheme ? colors.iconActive : undefined

  const generateBulletPointComponent = (key: string, value: { defaultMessage: string; description: string }) => {
    return {
      value: (
        <BulletPointContainer testID={tID(`${testID}-${key}`)}>
          <BodyText
            text={<FormattedMessage {...value} />}
            size={isMinWidthLaptop ? BodyTextSize.LARGE : BodyTextSize.DEFAULT}
          />
        </BulletPointContainer>
      ),
      bulletIcon: <CheckMarkIcon fillColor={checkMarkFillColor} stroke={checkMarkFillColor} />,
    }
  }

  const lyraBulletPoints = Object.keys(VALUE_PROPS_TITLES)
    .filter((key) => !key.includes('condensed'))
    .map((key) => generateBulletPointComponent(key, VALUE_PROPS_TITLES[key]))

  const welcomeToLyraHeader =
    isAuroraTheme && isMobileSized ? (
      <Display
        testID={tID(`${testID}-welcomeToLyraHeader`)}
        text={formatMessage(WELCOME_TO_LYRA_HEADER_TEXT)}
        size={DisplaySize.SMALL}
      />
    ) : (
      <MobileSizedHeader
        testID={tID(`${testID}-welcomeToLyraHeader`)}
        text={formatMessage(WELCOME_TO_LYRA_HEADER_TEXT)}
        size={TitleSize.LARGE}
      />
    )

  const letsGetStartedHeader =
    isAuroraTheme && isMobileSized ? (
      <Display
        testID={tID(`${testID}-letsGetStartedHeader`)}
        text={formatMessage(LETS_GET_STARTED_HEADER_TEXT)}
        size={DisplaySize.SMALL}
      />
    ) : (
      <MobileSizedHeader
        testID={tID(`${testID}-letsGetStartedHeader`)}
        text={formatMessage(LETS_GET_STARTED_HEADER_TEXT)}
        size={TitleSize.LARGE}
      />
    )

  const findEmployerInputField = <FindEmployerInputField testID={testID} />

  const careNavigatorSupportText = (
    <CareNavigatorSupportTextContainer testID={tID(`${testID}-careNavigatorSupportText`)}>
      {isMobileSized ? (
        <>
          <BodyText
            size={BodyTextSize.DEFAULT}
            color={colors.textPrimary}
            text={formatMessage({
              defaultMessage: 'Have questions or need help signing up?',
              description: 'Text prompting users to contact a Care Navigator for help signing up',
            })}
          />
          <Link
            text={formatMessage({
              defaultMessage: 'Contact a Care Navigator',
              description: 'Link text to contact a Care Navigator via email',
            })}
            onPress={() => mailTo(CARE_ADVISOR_EMAIL)}
            underline
            size={BodyTextSize.DEFAULT}
            testID={tID(`${testID}-contactCareNavigator-link`)}
            accessibilityLabel={formatMessage({
              defaultMessage: 'Contact a Care Navigator by email',
              description: 'Accessibility label for Care Navigator email link',
            })}
          />
        </>
      ) : (
        <BodyText
          size={BodyTextSize.DEFAULT}
          color={colors.textPrimary}
          text={
            <FormattedMessage
              defaultMessage='Have questions or need help signing up? <link>Contact a Care Navigator</link>'
              description='Subtext below the continue button prompting users to contact a Care Navigator via email for help signing up'
              values={{
                link: (chunks: React.ReactNode) => (
                  <Link
                    text={chunks}
                    onPress={() => mailTo(CARE_ADVISOR_EMAIL)}
                    underline
                    size={BodyTextSize.DEFAULT}
                    testID={tID(`${testID}-contactCareNavigator-link`)}
                    accessibilityLabel={formatMessage({
                      defaultMessage: 'Contact a Care Navigator by email',
                      description: 'Accessibility label for Care Navigator email link',
                    })}
                  />
                ),
              }}
            />
          }
        />
      )}
    </CareNavigatorSupportTextContainer>
  )

  const generateFormContent = (handleSubmit: (event: GestureResponderEvent) => void) => {
    return (
      <OrganizationInputFormContent>
        {findEmployerInputField}
        <PrimaryButton
          testID={tID(`${testID}-findEmployerInputField-submit`)}
          onPress={handleSubmit}
          text={formatMessage(CONTINUE_BUTTON_TEXT)}
          fullWidth
        />
      </OrganizationInputFormContent>
    )
  }

  const MobileSizedWrapperComponent = IS_WEB ? React.Fragment : FixedButtonFormPageTemplate
  const mobileSizedWrapperProps = IS_WEB
    ? {}
    : {
        onSubmitPress: () => {
          return submit.current()
        },
        testID,
        buttonText: formatMessage(CONTINUE_BUTTON_TEXT),
        isButtonTransparent: true,
        loading,
        navBarProps,
        overlayHeader: true,
        ignoreTopInset: !isAuroraTheme,
        stickyViewBackgroundColor: isAuroraTheme ? colors.backgroundTransparent : undefined,
        buttonContainerCustomStyle: isAuroraTheme
          ? {
              border: 0,
              borderTopWidth: 0,
              boxShadow: 'none',
              backgroundColor: colors.backgroundTransparent,
            }
          : undefined,
      }

  const windowHeight = useMemo(() => Dimensions.get('window').height, [])
  // setting a minimum height for non-mobile sized screens so that the main portion of the background remains viewable
  const minimumHeight = 900

  const desktopFallbackBackground = {
    component: FindEmployerContentContainerDesktopSized,
    props: {
      testID: tID(`${testID}-desktopSizedContainer`),
      source: (isMinWidthDesktop ? DawnBackgroundWiderAspectRatio : DawnBackground) as ImageSourcePropType,
      resizeMode: 'cover' as const,
    },
  }

  const desktopContentElements = (
    <>
      <LyraBulletPointsContainer>
        <BulletPointsTitle
          size={TitleSize.MEDIUM}
          text={
            <FormattedMessage
              defaultMessage="What's included with Lyra"
              description='Section title for Lyra benefits'
            />
          }
        />
        <BulletedList data={lyraBulletPoints} centerAlignBullet spaceBetween={isAuroraTheme ? 24 : 10} />
      </LyraBulletPointsContainer>
      <DesktopCardAndSupportTextContainer>
        <OrganizationInputFloatingContainer style={floatingStyles.floatingCard}>
          {isMinWidthLaptop ? (
            <OrganizationInputFloatingHeader
              testID={tID(`${testID}-welcomeToLyraHeader-floating`)}
              text={formatMessage(WELCOME_TO_LYRA_HEADER_TEXT)}
              size={DisplaySize.SMALL}
            />
          ) : (
            welcomeToLyraHeader
          )}
          <Form
            onSubmit={onSubmit}
            render={({ handleSubmit }) => {
              submit.current = handleSubmit
              return generateFormContent(handleSubmit)
            }}
          />
        </OrganizationInputFloatingContainer>
        {isDbtReferralEnabled && careNavigatorSupportText}
      </DesktopCardAndSupportTextContainer>
    </>
  )

  const renderDesktopContent = () =>
    isAuroraTheme ? (
      <AuroraDesktopContentContainer>{desktopContentElements}</AuroraDesktopContentContainer>
    ) : (
      desktopContentElements
    )

  const mobileContent = (
    <FindEmployerContainer
      testID={tID(testID)}
      height={isMobileSized || windowHeight > minimumHeight ? windowHeight : minimumHeight}
    >
      <FindEmployerContentContainerMobileSized testID={tID(`${testID}-mobileSizedContainer`)}>
        {/* @ts-expect-error TS(2322): Type Element[]; onSubmitPress?: undefined;... Remove this comment to see the full error message */}
        <MobileSizedWrapperComponent {...mobileSizedWrapperProps}>
          {!isAuroraTheme && (
            <DawnImageContainer>
              <DawnImageMobileSized
                testID={tID(`${testID}-dawnImage-mobileSized`)}
                source={(IS_WEB ? DawnBackgroundMobileWeb : DawnBackgroundMobile) as ImageSourcePropType}
                accessibilityIgnoresInvertColors
                updatedHeight={!IS_WEB ? Dimensions.get('window').height * 0.4 : undefined}
                contentFit='cover'
              />
            </DawnImageContainer>
          )}
          <MobileSizedFormContainer>
            {IS_WEB ? (
              <>
                {welcomeToLyraHeader}
                <DescriptionTextContainer>
                  <BodyText
                    testID={tID(`${testID}-lyraOffers-description`)}
                    text={
                      <FormattedMessage
                        defaultMessage='Lyra offers 100% confidential care for individuals and their family, a diverse provider network, and 24/7 Care Navigator support.'
                        description='Short description of what Lyra provides.'
                      />
                    }
                    color={isAuroraTheme ? colors.textSecondary : colors.textInactive}
                  />
                </DescriptionTextContainer>
                <Form
                  onSubmit={onSubmit}
                  render={({ handleSubmit }) => {
                    submit.current = handleSubmit
                    return generateFormContent(handleSubmit)
                  }}
                />
                {isDbtReferralEnabled && careNavigatorSupportText}
              </>
            ) : (
              <>
                {letsGetStartedHeader}
                <NativeMobileFormSpacing>
                  <Form
                    onSubmit={onSubmit}
                    render={({ handleSubmit }) => {
                      submit.current = handleSubmit
                      return findEmployerInputField
                    }}
                  />
                </NativeMobileFormSpacing>
                {isDbtReferralEnabled && careNavigatorSupportText}
              </>
            )}
          </MobileSizedFormContainer>
        </MobileSizedWrapperComponent>
      </FindEmployerContentContainerMobileSized>
      <InputAccessory nativeID={testID} />
      <StatusBar style='dark' />
    </FindEmployerContainer>
  )

  const desktopContent = (
    <FindEmployerContainer
      testID={tID(testID)}
      height={isMobileSized || windowHeight > minimumHeight ? windowHeight : minimumHeight}
    >
      {renderDesktopContent()}
      <InputAccessory nativeID={testID} />
      <StatusBar style='dark' />
    </FindEmployerContainer>
  )

  if (isMobileSized) {
    return <AuroraAwareBackground>{mobileContent}</AuroraAwareBackground>
  }

  return <AuroraAwareBackground fallbackBackground={desktopFallbackBackground}>{desktopContent}</AuroraAwareBackground>
}
